import { useState, useEffect, useRef } from 'react';
import { checkServerVersion, forceAppUpdate } from '@/lib/appUpdate';
import { Button } from '@/components/ui/button';
import { RefreshCw, X } from 'lucide-react';

const AUTO_APPLY_SECONDS = 30;

/**
 * Mid-session update banner.
 *
 * Triggered when a deploy happens WHILE the user has the app open (not at
 * login — that is handled silently by initAppVersionCheck in App.js).
 *
 * Behavior:
 *  - Polls /api/app-version every 5 minutes.
 *  - On mismatch: shows a non-missable blue bar with a 30-second countdown.
 *  - User can click "Update Now" to apply immediately, OR "Later" to get a
 *    1-hour snooze. If they do nothing, it auto-applies when the countdown
 *    hits zero (prevents users from accidentally running stale code for
 *    hours after a release).
 */
export function UpdateBanner() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [snoozedUntil, setSnoozedUntil] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [remaining, setRemaining] = useState(AUTO_APPLY_SECONDS);
  const autoApplyTimer = useRef(null);

  useEffect(() => {
    const check = async () => {
      // Respect snooze
      if (Date.now() < snoozedUntil) return;
      const result = await checkServerVersion();
      if (result.updateAvailable) {
        setUpdateAvailable(true);
        setRemaining(AUTO_APPLY_SECONDS);
      }
    };
    check();
    const interval = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [snoozedUntil]);

  // Countdown tick while visible
  useEffect(() => {
    if (!updateAvailable || updating) return;
    if (remaining <= 0) {
      handleUpdate();
      return;
    }
    autoApplyTimer.current = setTimeout(() => setRemaining((r) => r - 1), 1000);
    return () => clearTimeout(autoApplyTimer.current);
    // Intentionally re-run on remaining/updateAvailable/updating changes only
  }, [remaining, updateAvailable, updating]);

  const handleUpdate = async () => {
    if (updating) return;
    setUpdating(true);
    await forceAppUpdate();
  };

  const handleSnooze = () => {
    // 1-hour snooze so active users aren't disrupted mid-task, but the
    // banner WILL re-appear next check.
    setUpdateAvailable(false);
    setSnoozedUntil(Date.now() + 60 * 60 * 1000);
  };

  if (!updateAvailable) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[200] bg-blue-600 text-white px-4 py-2 flex flex-wrap items-center justify-center gap-3 shadow-lg"
      data-testid="update-banner"
      role="status"
      aria-live="polite"
    >
      <RefreshCw className={`w-4 h-4 ${updating ? 'animate-spin' : 'animate-pulse'}`} />
      <span className="text-sm font-medium">
        A new version of WorkForce Pro is available
        {!updating && (
          <>
            {' '}— applying in <span className="font-bold" data-testid="update-countdown">{remaining}</span>s
          </>
        )}
      </span>
      <Button
        size="sm"
        variant="secondary"
        className="text-xs bg-white text-blue-700 hover:bg-blue-50"
        onClick={handleUpdate}
        disabled={updating}
        data-testid="update-now-btn"
      >
        {updating ? 'Updating…' : 'Update Now'}
      </Button>
      <button
        onClick={handleSnooze}
        disabled={updating}
        className="text-xs text-white/80 hover:text-white underline disabled:opacity-40"
        data-testid="update-snooze-btn"
        title="Snooze for 1 hour"
      >
        Later
      </button>
      <button
        onClick={handleSnooze}
        disabled={updating}
        className="ml-1 text-white/70 hover:text-white disabled:opacity-40"
        aria-label="Snooze for 1 hour"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default UpdateBanner;
